import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { NavLink, withRouter } from "react-router-dom";

import { Badge, Collapse } from "reactstrap";
import PerfectScrollbar from "react-perfect-scrollbar";

// UI Components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import SidebarCampaignFolders from "./Sidebar.CampaignFolders";

import routes from "../routes/index";
import logo from "../assets/img/brands/mb-square-logo.png";

const SidebarCategory = withRouter(
  ({ name, badgeColor, badgeText, icon: Icon, isOpen, children, onClick, location, to }) => {
    const getSidebarItemClass = (path) => {
      return location.pathname.indexOf(path) !== -1 ||
        (location.pathname === "/" && path === "/dashboard")
        ? "active"
        : "";
    };

    return (
      <li className={"sidebar-item " + getSidebarItemClass(to)}>
        <span
          data-toggle="collapse"
          className={"sidebar-link " + (!isOpen ? "collapsed" : "")}
          onClick={onClick}
          aria-expanded={isOpen ? "true" : "false"}
        >
          <Icon size={18} className="align-middle mr-3" />

          <span className="align-middle">{name}</span>

          {badgeColor && badgeText ? (
            <Badge color={badgeColor} size={18} className="sidebar-badge">
              {badgeText}
            </Badge>
          ) : null}
        </span>

        <Collapse isOpen={isOpen}>
          <ul id="item" className={"sidebar-dropdown list-unstyled"}>
            {children}
          </ul>
        </Collapse>
      </li>
    );
  }
);

const SidebarItem = withRouter(({ name, badgeColor, badgeText, icon: Icon, location, to }) => {
  const getSidebarItemClass = (path) => {
    return location.pathname === path ? "active" : "";
  };

  return (
    <li className={"sidebar-item " + getSidebarItemClass(to)}>
      <NavLink to={to} className="sidebar-link" activeClassName="active">
        {Icon ? <Icon size={18} className="align-middle mr-3" /> : null}
        {name}
        {badgeColor && badgeText ? (
          <Badge color={badgeColor} size={18} className="sidebar-badge">
            {badgeText}
          </Badge>
        ) : null}
      </NavLink>
    </li>
  );
});

class Sidebar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  toggle = (index) => {
    // Collapse all elements
    Object.keys(this.state).forEach(
      (item) =>
        this.state[index] ||
        this.setState(() => ({
          [item]: false,
        }))
    );

    // Toggle selected element
    this.setState((state) => ({
      [index]: !state[index],
    }));
  };

  UNSAFE_componentWillMount() {
    /* Open collapse element that matches current url */
    const pathName = this.props.location.pathname;

    routes.forEach((route, index) => {
      const isActive = pathName.indexOf(route.path) === 0;
      const isOpen = route.open;
      const isHome = route.containsHome && pathName === "/" ? true : false;

      this.setState(() => ({
        [index]: isActive || isOpen || isHome,
      }));
    });
  }

  render() {
    const { sidebar, layout, user, loading } = this.props;

    return (
      <nav
        className={
          "sidebar" +
          (!sidebar.isOpen ? " toggled" : "") +
          (sidebar.isSticky ? " sidebar-sticky" : "")
        }
      >
        <div className="sidebar-content">
          <PerfectScrollbar>
            <a className="sidebar-brand" href="/">
              <img
                src={logo}
                alt="Message Broadcast"
                className="align-middle img-fluid mr-2"
                width="24"
              />
              <span className="align-middle">EONS</span>
            </a>

            <ul className="sidebar-nav">
              {routes.map((category, index) => {
                // Hide routes from users with permissions below the restricted level if set
                if (loading || user.group_id_bint < category.restrictedToLevel) {
                  return null;
                }
                // Hide admin sub-routes from admin-users below the required level
                let children = category.children; // Store here to prevent mutation
                if (children) {
                  children = children.filter(
                    (child) =>
                      !child.restrictedToLevel || user.group_id_bint >= child.restrictedToLevel
                  );
                }

                // If no valid children remain, hide this option (prevent accidental empty folders)
                // Campaigns are exception for alt-render of folders
                if (category.name !== "Campaigns" && category.children && !children.length) {
                  return null;
                }

                return (
                  <React.Fragment key={index}>
                    {category.header ? <li className="sidebar-header">{category.header}</li> : null}

                    {children ? (
                      <SidebarCategory
                        name={category.name}
                        badgeColor={category.badgeColor}
                        badgeText={category.badgeText}
                        icon={category.icon}
                        to={category.link || category.path}
                        isOpen={this.state[index]}
                        onClick={() => this.toggle(index)}
                      >
                        {children.map((route, index) => (
                          <SidebarItem
                            key={index}
                            name={route.name}
                            to={route.link || route.path}
                            badgeColor={route.badgeColor}
                            badgeText={route.badgeText}
                          />
                        ))}

                        {category.name === "Campaigns" && <SidebarCampaignFolders />}
                      </SidebarCategory>
                    ) : (
                      <SidebarItem
                        name={category.name}
                        to={category.link || category.path}
                        icon={category.icon}
                        badgeColor={category.badgeColor}
                        badgeText={category.badgeText}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </ul>

            {/* TODO: Update this */}
            {!layout.isBoxed && !sidebar.isSticky ? (
              <div className="sidebar-bottom d-none d-lg-block">
                <div className="media">
                  <img
                    className="rounded-circle mr-3"
                    src={logo}
                    alt="Message Broadcast"
                    width="40"
                    height="40"
                  />
                  <div className="media-body">
                    <h5 className="mb-1">{user.firstname_vch || "User"}</h5>
                    <div>
                      <FontAwesomeIcon icon={faCircle} className="text-success" /> Online
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </PerfectScrollbar>
        </div>
      </nav>
    );
  }
}

Sidebar.propTypes = {
  loading: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  sidebar: state.sidebar,
  layout: state.layout,
  user: state.user.user,
  loading: state.user.loading,
});

export default withRouter(connect(mapStateToProps)(Sidebar));
